import React, { useState, useMemo } from 'react';
import { HiOutlineClock } from 'react-icons/hi';
import { useAppShell } from '../Layout/AppShell';
import { hasCardAction } from '../../utils/permissionHelper';
import ReportTab from './ReportTab';
import AttendanceTab from './AttendanceTab';
import SheetTab from './SheetTab';
import DevicesTab from './DevicesTab';
import ConfigureTab from './ConfigureTab';
import MyToday from './MyToday';
import MyHistory from './MyHistory';
import './WorkingTime.css';

const ADMIN_TABS = [
    { id: 'daily', label: 'Daily' },
    { id: 'monthly', label: 'Monthly' },
    { id: 'attendance', label: 'Attendance' },
    { id: 'sheet', label: 'Sheet' },
    { id: 'devices', label: 'Devices' },
    { id: 'configure', label: 'Configure' }
];

const SELF_TABS = [
    { id: 'today', label: 'Today' },
    { id: 'history', label: 'History' }
];

/**
 * One page, two faces.
 *
 * An admin is also an employee — they carry a laptop agent and check in/out like
 * everyone else — so they get the self tabs (Today, History) *and* the
 * company-wide ones. A non-admin gets only the self tabs; that is cosmetic on
 * top of requireWorkTimeAdmin, which refuses the company routes regardless of
 * what the browser asks for.
 */
export default function WorkingTime() {
    const { permissions, isAdmin } = useAppShell();

    const isWtAdmin = useMemo(
        () => isAdmin || hasCardAction(permissions, 'working-time', 'approve'),
        [isAdmin, permissions]
    );

    const tabs = isWtAdmin ? [...SELF_TABS, ...ADMIN_TABS] : SELF_TABS;
    const [tab, setTab] = useState(tabs[0].id);

    return (
        <div className="wt-page">
            <header className="wt-header">
                <HiOutlineClock className="wt-header-icon" />
                <div>
                    <h1>{isWtAdmin ? 'Working Time' : 'My Working Time'}</h1>
                    <p>
                        {isWtAdmin
                            ? 'Active and idle time reported by the Windows agent'
                            : 'Your own active and idle time, reported by the Windows agent'}
                    </p>
                </div>
            </header>

            <nav className="wt-tabs">
                {tabs.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        className={`wt-tab${tab === t.id ? ' active' : ''}`}
                        onClick={() => setTab(t.id)}
                    >
                        {t.label}
                    </button>
                ))}
            </nav>

            {/* key forces a remount so switching tabs resets the period control */}
            {tab === 'today' && <MyToday />}
            {tab === 'history' && <MyHistory />}

            {isWtAdmin && tab === 'daily' && <ReportTab key="daily" mode="daily" />}
            {isWtAdmin && tab === 'monthly' && <ReportTab key="monthly" mode="monthly" />}
            {isWtAdmin && tab === 'attendance' && <AttendanceTab />}
            {isWtAdmin && tab === 'sheet' && <SheetTab />}
            {isWtAdmin && tab === 'devices' && <DevicesTab />}
            {isWtAdmin && tab === 'configure' && <ConfigureTab />}
        </div>
    );
}
